"use client";
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Tabs, TabsContent } from "../../../components/ui/tabs";
import { Button } from "../../../components/ui/button";
import { Order } from "../types/order";
import { OrderCard } from "./OrderCard";
import { OrderForm } from "./OrderForm";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { Plus, ShoppingCart, Filter, X, Download } from "lucide-react";
import { ListPaginationFooter } from "../../../components/ListPaginationFooter";
import { exportOrdersToCsv, deliveryColumnLabelForRoles } from "../../../utils/ordersCsv";
import { normalizeCustomerRole } from "../../../utils/dateFormat";
import { formatRoleLabel } from "../../../utils/roles";
import { PANEL_DATA_CHANGED, type PanelDataChange } from "../../../utils/panelDataSync";

type CustomerRoleOption = "dealer" | "end-user" | "interior";

export const OrdersPage: React.FC<{ userRole?: "admin" | "dealer" | "employee"; userId?: string }> = ({
  userRole: userRoleProp,
  userId: userIdProp,
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [tab, setTab] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [createdBy, setCreatedBy] = useState<string>("");
  const [customer, setCustomer] = useState<string>("");
  const [fromDate, setFromDate] = useState<string>("");
  const [toDate, setToDate] = useState<string>("");
  const [exporting, setExporting] = useState(false);
  const [exportingDelivery, setExportingDelivery] = useState(false);

  // Advanced delivery/fixing filter (does not replace the existing filter UI)
  const [advRoles, setAdvRoles] = useState<CustomerRoleOption[]>([]);
  const [advCustomer, setAdvCustomer] = useState<string>("");
  const [advCreatedBy, setAdvCreatedBy] = useState<string>("");
  const [advFromDate, setAdvFromDate] = useState<string>("");
  const [advToDate, setAdvToDate] = useState<string>("");
  const [filterMode, setFilterMode] = useState<"standard" | "delivery">("standard");

  const [users, setUsers] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [showAdvFilters, setShowAdvFilters] = useState(false);
  const [loading, setLoading] = useState(true);
  const { authToken = "", id = "", role = "", username = "" } = useSelector(
    (state: any) => state.loginData || {}
  );
  const userRole = (userRoleProp || role || "admin") as "admin" | "dealer" | "employee";
  const userId = String(userIdProp || id || "");
  const isAdmin = String(role || userRole).toLowerCase() === "admin";
  const isStaff = String(role || userRole).toLowerCase() === "employee";
  const isDealer = String(role || userRole).toLowerCase() === "dealer";

  const maxResults = 10;

  const advRoleSelectValue = useMemo(() => {
    if (advRoles.length === 0) return "";
    if (advRoles.includes("dealer")) return "dealer";
    if (advRoles.includes("end-user") && advRoles.includes("interior")) return "end-user,interior";
    if (advRoles.includes("end-user")) return "end-user";
    if (advRoles.includes("interior")) return "interior";
    return "";
  }, [advRoles]);

  const filteredCustomersForAdv = useMemo(() => {
    if (advRoles.length === 0) return customers;
    return customers.filter((u) => {
      const r = normalizeCustomerRole(u.role);
      return advRoles.some((selected) => {
        if (selected === "end-user") return r === "end-user" || r === "enduser";
        return r === selected;
      });
    });
  }, [customers, advRoles]);

  const deliveryDateLabel = deliveryColumnLabelForRoles(advRoles);

  const creatorUsers = useMemo(
    () =>
      users.filter((user) => {
        const r = String(user.role || "")
          .trim()
          .toLowerCase()
          .replace(/_/g, "-");
        return r === "admin" || r === "employee" || r === "dealer";
      }),
    [users]
  );

  const standardFilterParams = {
    status: tab,
    createdBy,
    customer,
    fromDate,
    toDate,
  };

  useEffect(() => {
    if (!isAdmin) {
      setAdvCreatedBy(username || "");
      return;
    }
    if (!advCreatedBy) return;
    const stillValid = creatorUsers.some((u) => u.username === advCreatedBy);
    if (!stillValid) setAdvCreatedBy("");
  }, [creatorUsers, advCreatedBy, isAdmin, username]);

  // Staff: never show delivery Created By UI; lock to self for API
  // Dealer: no delivery filter at all
  useEffect(() => {
    if (isDealer && showAdvFilters) setShowAdvFilters(false);
  }, [isDealer, showAdvFilters]);

  /** Admin may pick any creator; staff is locked to self for delivery queries. */
  const deliveryCreatedBy = isAdmin ? advCreatedBy : isStaff ? username || "" : "";

  const setAdvRolesFromSelect = (value: string) => {
    if (!value) {
      setAdvRoles([]);
    } else if (value === "dealer") {
      setAdvRoles(["dealer"]);
    } else if (value === "end-user,interior") {
      setAdvRoles(["end-user", "interior"]);
    } else if (value === "end-user" || value === "interior") {
      setAdvRoles([value]);
    }
    setAdvCustomer("");
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params: { firstResults: 0, maxResults: "all", role: "creator" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUsers(res.data.users || []);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  /** Staff filters: only customers who appear on orders this staff created. */
  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params: { firstResults: 0, maxResults: "all", role: "all" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      let list: any[] = res.data.users || [];

      if (String(role || userRole).toLowerCase() === "employee" && username) {
        try {
          const ordersRes = await axios.get(`${API_BASE_URL}account/${id}/orders`, {
            params: {
              firstResults: 0,
              maxResults: 5000,
              status: "all",
              lite: true,
              createdBy: username,
            },
            headers: { Authorization: `Bearer ${authToken}` },
          });
          const ids = new Set(
            (ordersRes.data.orders || []).map((o: any) => String(o.customerId || ""))
          );
          list = list.filter((u) => ids.has(String(u.id)));
        } catch (e) {
          console.error("Error loading staff customers from orders:", e);
        }
      }

      setCustomers(list);
    } catch (error) {
      console.error("Error fetching customers:", error);
    }
  };

  useEffect(() => {
    if (!id || !authToken) return;
    fetchUsers();
    fetchCustomers();
  }, [id, authToken, role, username]);

  const deliveryFilterParams = () => {
    const params: Record<string, string | number> = {
      firstResults: 0,
      maxResults,
      status: tab,
      customerRoles: advRoles.join(","),
    };
    if (deliveryCreatedBy) params.createdBy = deliveryCreatedBy;
    if (advCustomer) params.customer = advCustomer;
    if (advFromDate) params.deliveryFromDate = advFromDate;
    if (advToDate) params.deliveryToDate = advToDate;
    return params;
  };

  const fetchOrders = useCallback(
    async (pageNum = 1, mode: "standard" | "delivery" = filterMode) => {
      if (!id || !authToken) return;
      setLoading(true);
      try {
        const firstResults = (pageNum - 1) * maxResults;
        const params =
          mode === "delivery"
            ? { ...deliveryFilterParams(), firstResults, maxResults, lite: true }
            : {
                firstResults,
                maxResults,
                status: tab,
                lite: true,
                createdBy,
                customer,
                fromDate,
                toDate,
              };

        const res = await axios.get(`${API_BASE_URL}account/${id}/orders`, {
          params,
          headers: { Authorization: `Bearer ${authToken}` },
        });

        setOrders(res.data.orders || []);
        setTotalCount(res.data.totalCount || 0);
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    },
    [
      id,
      authToken,
      filterMode,
      tab,
      createdBy,
      customer,
      fromDate,
      toDate,
      maxResults,
      advRoles,
      advCustomer,
      advFromDate,
      advToDate,
      deliveryCreatedBy,
    ]
  );

  const fetchOrdersRef = useRef(fetchOrders);
  fetchOrdersRef.current = fetchOrders;
  const pageRef = useRef(page);
  pageRef.current = page;
  const filterModeRef = useRef(filterMode);
  filterModeRef.current = filterMode;

  useEffect(() => {
    const onPanelDataChanged = (event: Event) => {
      const detail = (event as CustomEvent<PanelDataChange>).detail;
      if (!detail?.orders) return;
      if (detail.resetOrdersPage) {
        setPage(1);
        void fetchOrdersRef.current(1, filterModeRef.current);
      } else {
        void fetchOrdersRef.current(pageRef.current, filterModeRef.current);
      }
    };
    window.addEventListener(PANEL_DATA_CHANGED, onPanelDataChanged);
    return () => window.removeEventListener(PANEL_DATA_CHANGED, onPanelDataChanged);
  }, []);

  const exportWithParams = async (
    params: Record<string, string | boolean | number>,
    opts: {
      prefix: string;
      fromDate?: string;
      toDate?: string;
      customerRoles?: string[];
      deliveryColumnName?: string;
    }
  ) => {
    const countRes = await axios.get(`${API_BASE_URL}account/${id}/orders`, {
      params: { firstResults: 0, maxResults: 1, ...params },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    const filteredCount = countRes.data.totalCount || 0;
    if (filteredCount === 0) {
      window.alert("No orders to export for the current filters.");
      return;
    }
    const res = await axios.get(`${API_BASE_URL}account/${id}/orders`, {
      params: { firstResults: 0, maxResults: filteredCount, ...params },
      headers: { Authorization: `Bearer ${authToken}` },
    });
    const exportOrders: Order[] = res.data.orders || [];
    if (exportOrders.length === 0) {
      window.alert("No orders to export for the current filters.");
      return;
    }
    exportOrdersToCsv(exportOrders, opts);
  };

  const handleExportCsv = async () => {
    setExporting(true);
    try {
      await exportWithParams(
        { ...standardFilterParams },
        { prefix: "orders", fromDate, toDate }
      );
    } catch (error) {
      console.error("Error exporting orders CSV:", error);
      window.alert("Failed to export orders. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  const handleExportDeliveryCsv = async () => {
    if (advRoles.length === 0) {
      window.alert("Select at least one user type (Dealer, or End User / Interior).");
      return;
    }
    setExportingDelivery(true);
    try {
      await exportWithParams(
        {
          status: tab,
          ...(deliveryCreatedBy ? { createdBy: deliveryCreatedBy } : {}),
          ...(advCustomer ? { customer: advCustomer } : {}),
          customerRoles: advRoles.join(","),
          ...(advFromDate ? { deliveryFromDate: advFromDate } : {}),
          ...(advToDate ? { deliveryToDate: advToDate } : {}),
        },
        {
          prefix: "orders_delivery",
          fromDate: advFromDate,
          toDate: advToDate,
          customerRoles: advRoles,
          deliveryColumnName: deliveryDateLabel,
        }
      );
    } catch (error) {
      console.error("Error exporting delivery CSV:", error);
      window.alert("Failed to export. Please try again.");
    } finally {
      setExportingDelivery(false);
    }
  };

  useEffect(() => {
    setPage(1);
    fetchOrders(1, filterMode);
  }, [tab, filterMode, createdBy, customer, fromDate, toDate, fetchOrders]);

  const totalPages = Math.ceil(totalCount / maxResults);

  const handlePrev = () => {
    if (page > 1) {
      const newPage = page - 1;
      setPage(newPage);
      fetchOrders(newPage);
    }
  };

  const handleNext = () => {
    if (page < totalPages) {
      const newPage = page + 1;
      setPage(newPage);
      fetchOrders(newPage);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
            <ShoppingCart className="text-blue-600" size={36} />
            Orders Management
          </h1>
          <p className="text-gray-500 text-sm mt-2">Manage all your orders with advanced filters and search</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          {!isDealer && (
            <Button
              variant="outline"
              onClick={handleExportCsv}
              disabled={exporting}
              className="flex items-center gap-2 py-3 px-6 rounded-xl border-2 border-gray-300 w-full sm:w-auto disabled:opacity-50"
            >
              <Download size={18} />
              {exporting ? "Exporting…" : "Export CSV"}
            </Button>
          )}
          <Button
            onClick={() => {
              setSelectedOrder(null);
              setShowForm(true);
            }}
            className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white flex items-center gap-2 py-3 px-6 rounded-xl shadow-lg shadow-blue-600/30 w-full sm:w-auto"
          >
            <Plus size={20} />
            Create Order
          </Button>
        </div>
      </div>

      <div>
        <div className="bg-white border-2 border-gray-200 rounded-xl p-4 flex flex-col gap-3 lg:flex-row lg:flex-wrap xl:flex-nowrap lg:items-end">
          {isAdmin && (
            <div className="flex flex-col w-full lg:flex-1 lg:min-w-[140px]">
              <label className="text-sm font-medium text-gray-600 mb-1">Created By</label>
              <select
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 h-10 text-sm bg-white w-full"
              >
                <option value="">All</option>
                {creatorUsers.map((user) => (
                  <option key={user.id} value={user.username}>
                    {user.username}
                    {user.role ? ` (${formatRoleLabel(user.role)})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          {!isDealer && (
            <div className="flex flex-col w-full lg:flex-1 lg:min-w-[140px]">
              <label className="text-sm font-medium text-gray-600 mb-1">Customer</label>
              <select
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 h-10 text-sm bg-white w-full"
              >
                <option value="">All</option>
                {customers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.username}
                    {user.role ? ` (${formatRoleLabel(user.role)})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex flex-col w-full sm:w-[calc(50%-0.375rem)] lg:w-[150px] lg:flex-none">
            <label className="text-sm font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 h-10 text-sm bg-white w-full"
            />
          </div>

          <div className="flex flex-col w-full sm:w-[calc(50%-0.375rem)] lg:w-[150px] lg:flex-none">
            <label className="text-sm font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 h-10 text-sm bg-white w-full"
            />
          </div>

          <div className="w-full lg:w-auto lg:flex-none flex flex-row gap-2 sm:gap-3">
            <Button
              onClick={() => {
                setFilterMode("standard");
                setPage(1);
                fetchOrders(1, "standard");
              }}
              className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white h-10 px-5 rounded-lg shadow-md shadow-blue-600/25 flex-1 sm:flex-none whitespace-nowrap"
            >
              Apply
            </Button>
            {!isDealer && (
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowAdvFilters((prev) => !prev)}
                title={
                  showAdvFilters
                    ? "Hide Company Delivery / Fixing Date Filter"
                    : "Show Company Delivery / Fixing Date Filter"
                }
                className={`h-10 px-4 rounded-lg border-2 flex-1 sm:flex-none whitespace-nowrap gap-2 ${
                  showAdvFilters
                    ? "border-emerald-500 bg-emerald-50 text-emerald-700"
                    : "border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                }`}
              >
                {showAdvFilters ? <X size={16} className="shrink-0" /> : <Filter size={16} className="shrink-0" />}
                <span>{showAdvFilters ? "Hide" : "Delivery Filter"}</span>
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Company Delivery / Fixing Date filter — admin + staff only (not dealers) */}
      {showAdvFilters && !isDealer && (
        <div className="bg-white border-2 border-emerald-200 rounded-xl p-5">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-base font-bold text-gray-900">Company Delivery / Fixing Date Filter</h2>
              <p className="text-xs text-gray-500 mt-1 leading-relaxed max-w-3xl">
                Dealer → Company Delivery Date. End User / Interior → Fixing Date. Dealer cannot be
                combined with End User or Interior.
              </p>
            </div>
            {filterMode === "delivery" && (
              <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-lg whitespace-nowrap">
                Active: {deliveryDateLabel}
              </span>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 items-end">
            <div className="flex flex-col min-w-0">
              <label className="text-sm font-medium text-gray-600 mb-1.5">User Type</label>
              <select
                value={advRoleSelectValue}
                onChange={(e) => setAdvRolesFromSelect(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm h-11 bg-white w-full"
              >
                <option value="">Select user type</option>
                <option value="dealer">Dealer</option>
                <option value="end-user">End User</option>
                <option value="interior">Interior</option>
                <option value="end-user,interior">End User &amp; Interior</option>
              </select>
            </div>

            {isAdmin && (
              <div className="flex flex-col min-w-0">
                <label className="text-sm font-medium text-gray-600 mb-1.5">Created By</label>
                <select
                  value={advCreatedBy}
                  onChange={(e) => setAdvCreatedBy(e.target.value)}
                  className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm h-11 bg-white w-full"
                >
                  <option value="">All</option>
                  {creatorUsers.map((user) => (
                    <option key={user.id} value={user.username}>
                      {user.username}
                      {user.role ? ` (${formatRoleLabel(user.role)})` : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col min-w-0">
              <label className="text-sm font-medium text-gray-600 mb-1.5">Customer</label>
              <select
                value={advCustomer}
                onChange={(e) => setAdvCustomer(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm h-11 bg-white w-full"
              >
                <option value="">All customers</option>
                {filteredCustomersForAdv.map((user) => (
                  <option key={user.id} value={user.id}>
                    {(user.companyName || user.username)
                      + (user.userCode ? ` · ${user.userCode}` : "")
                      + (user.role ? ` · ${formatRoleLabel(user.role)}` : "")}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col min-w-0">
              <label className="text-sm font-medium text-gray-600 mb-1.5">
                From ({advRoles.length ? deliveryDateLabel : "Delivery / Fixing"})
              </label>
              <input
                type="date"
                value={advFromDate}
                onChange={(e) => setAdvFromDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm h-11 bg-white w-full"
              />
            </div>

            <div className="flex flex-col min-w-0">
              <label className="text-sm font-medium text-gray-600 mb-1.5">
                To ({advRoles.length ? deliveryDateLabel : "Delivery / Fixing"})
              </label>
              <input
                type="date"
                value={advToDate}
                onChange={(e) => setAdvToDate(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2.5 text-sm h-11 bg-white w-full"
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-2 min-w-0 sm:col-span-2 xl:col-span-3 2xl:col-span-1">
              <Button
                onClick={() => {
                  if (advRoles.length === 0) {
                    window.alert("Select a user type before applying.");
                    return;
                  }
                  setFilterMode("delivery");
                  setPage(1);
                  fetchOrders(1, "delivery");
                }}
                className="bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white h-11 px-4 rounded-xl whitespace-nowrap flex-1"
              >
                Apply
              </Button>
              <Button
                variant="outline"
                onClick={handleExportDeliveryCsv}
                disabled={exportingDelivery}
                className="flex items-center justify-center gap-2 h-11 px-4 rounded-xl border-2 disabled:opacity-50 whitespace-nowrap flex-1"
              >
                <Download size={16} />
                {exportingDelivery ? "Exporting…" : "CSV"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <div className="flex gap-2 overflow-x-auto pb-2">
          {["all", "saved", "new", "inprogress", "pending", "cancelled", "delivered", "completed"].map((tabValue) => {
            let bgColor = "bg-blue-600 text-white shadow-lg shadow-blue-600/30";

            if (tab === tabValue) {
              if (tabValue === "pending") {
                bgColor = "bg-yellow-500 text-white shadow-lg shadow-yellow-500/30";
              } else if (tabValue === "cancelled") {
                bgColor = "bg-red-600 text-white shadow-lg shadow-red-600/30";
              } else if (tabValue === "delivered") {
                bgColor = "bg-emerald-600 text-white shadow-lg shadow-emerald-600/30";
              } else if (tabValue === "inprogress") {
                bgColor = "bg-blue-600 text-white shadow-lg shadow-blue-600/30";
              } else if (tabValue === "saved") {
                bgColor = "bg-slate-600 text-white shadow-lg shadow-slate-600/30";
              }
            }

            return (
              <button
                key={tabValue}
                onClick={() => setTab(tabValue)}
                className={`px-6 py-2.5 rounded-xl font-semibold text-sm whitespace-nowrap transition-all ${
                  tab === tabValue ? bgColor : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {tabValue === "pending"
                  ? "Pending for cancellation"
                  : tabValue === "saved"
                  ? "Saved"
                  : tabValue.charAt(0).toUpperCase() + tabValue.slice(1)}
              </button>
            );
          })}
        </div>

        <TabsContent value={tab} className="space-y-4 mt-4">
          {loading && orders.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
              <ShoppingCart size={48} className="mx-auto text-gray-300 mb-3 animate-pulse" />
              <p className="text-gray-500 font-medium text-lg">Loading quotations…</p>
            </div>
          ) : orders.length === 0 ? (
            <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
              <ShoppingCart size={48} className="mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium text-lg">No orders found</p>
              <p className="text-gray-400 text-sm mt-1">Create a new order to get started</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {orders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  refresh={() => fetchOrders(page)}
                  onEdit={(order) => {
                    // Invoiced orders stay visible on Orders but must not open Edit
                    const invoiceGenerated =
                      order.invoiceGenerated === true ||
                      (order as Order & { invoice_generated_?: boolean }).invoice_generated_ === true;
                    if (invoiceGenerated) return;
                    setSelectedOrder(order);
                    setShowForm(true);
                  }}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <ListPaginationFooter
        page={page}
        totalPages={totalPages}
        totalCount={totalCount}
        currentCount={orders.length}
        itemLabel="orders"
        accent="blue"
        onPrev={handlePrev}
        onNext={handleNext}
      />

      <OrderForm
        open={showForm}
        onClose={() => setShowForm(false)}
        refresh={() => fetchOrders(page)}
        userRole={userRole}
        userId={userId}
        existingOrder={selectedOrder}
      />
    </div>
  );
};
